import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";

// Simple longevity calculator: how many hours you drive per week decides how
// many Booty Breeze fresheners you need per year. Anchored to the 3-4 week
// per-freshener claim used elsewhere.
const WEEKS_PER_FRESHENER = 3.5;   // average lifespan
const CAR_MONTHS_PER_YEAR = 12;

export default function LongevityCalculator() {
  const [hoursPerWeek, setHoursPerWeek] = useState(8);
  const [pricePer, setPricePer] = useState(8.99);

  // Higher driving = faster odor buildup = shorter lifespan. Scale between
  // 5 weeks (light driver, <5 hrs/wk) and 2 weeks (heavy driver, >20 hrs/wk).
  const estWeeks = useMemo(() => {
    const clamped = Math.max(1, Math.min(30, hoursPerWeek));
    // linear interpolation
    const t = (clamped - 5) / (20 - 5);        // 0 @ 5hrs, 1 @ 20hrs
    const life = 5 - Math.max(0, Math.min(1, t)) * 3;  // 5 → 2 weeks
    return Math.max(2, Math.min(5, life));
  }, [hoursPerWeek]);

  const fresheners = useMemo(() => Math.ceil(52 / estWeeks), [estWeeks]);
  const yearlyCost = fresheners * pricePer;
  const monthlyCost = yearlyCost / CAR_MONTHS_PER_YEAR;
  const treeCostYear = fresheners * 8 * 3;  // rough parity vs typical tree freshener replaced every 1 week @ $3

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Car Freshener Longevity Calculator",
    "url": "https://bootybreeze.com/longevity-calculator",
    "applicationCategory": "UtilityApplication",
    "description": "Estimate how many Booty Breeze air fresheners you'll need per year based on how much you drive.",
  };

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16" data-testid="longevity-page">
      <SEO
        title="Car Freshener Longevity Calculator — How Many Do You Need?"
        description="Enter your weekly driving hours and we'll tell you how many Booty Breeze fresheners you need per year — and how much you'd save vs. cheap tree fresheners."
        canonical="/longevity-calculator"
        jsonLd={jsonLd}
      />
      <div className="mb-6"><Link to="/" data-testid="longevity-home-link" className="text-white/50 hover:text-white text-xs uppercase tracking-widest">← Home</Link></div>
      <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-3 flex items-center gap-2"><Clock size={14} /> Freshener calculator</div>
      <h1 className="font-display text-4xl sm:text-5xl text-white mb-4">How many fresheners do you actually need?</h1>
      <p className="text-white/60 text-base mb-10 max-w-xl">Move the slider. We'll tell you exactly how many you need per year — and what you'd spend on cheap tree fresheners covering the same time.</p>

      <div className="rounded-2xl border border-white/10 bg-[#0f0f0f] p-8 sm:p-10" data-testid="longevity-card">
        <label className="block text-white/60 text-xs uppercase tracking-widest mb-3">How many hours per week do you drive?</label>
        <div className="flex items-center gap-6 mb-2">
          <input
            type="range" min={1} max={30} step={1}
            value={hoursPerWeek}
            onChange={(e) => setHoursPerWeek(parseInt(e.target.value))}
            data-testid="longevity-hours-slider"
            className="flex-1 accent-[#FF3D8B]"
          />
          <div className="text-4xl font-display text-white w-24 text-right" data-testid="longevity-hours-value">{hoursPerWeek}h</div>
        </div>
        <div className="text-white/40 text-xs mb-8">Short commute · Daily driver · Rideshare / delivery</div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="rounded-xl border border-white/10 bg-white/[.02] p-5" data-testid="longevity-lifespan">
            <div className="text-white/50 text-xs uppercase tracking-widest mb-2">Est. lifespan / freshener</div>
            <div className="font-display text-3xl text-white">{estWeeks.toFixed(1)} <span className="text-lg text-white/60">weeks</span></div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[.02] p-5" data-testid="longevity-fresheners">
            <div className="text-white/50 text-xs uppercase tracking-widest mb-2">You'll need / year</div>
            <div className="font-display text-3xl text-[#FF3D8B]">{fresheners}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[.02] p-5" data-testid="longevity-cost">
            <div className="text-white/50 text-xs uppercase tracking-widest mb-2">≈ Per month</div>
            <div className="font-display text-3xl text-white">${monthlyCost.toFixed(2)}</div>
          </div>
        </div>

        <div className="rounded-xl border border-[#A855F7]/30 bg-[#A855F7]/5 p-5 mb-6 flex items-start gap-3" data-testid="longevity-vs-tree">
          <Sparkles size={16} className="text-[#A855F7] mt-1 flex-shrink-0" />
          <div className="text-sm text-white/80 leading-relaxed">
            A cheap tree freshener lasts about a week and costs ~$3, so covering the same year would run
            <span className="font-semibold text-white"> ~${treeCostYear.toFixed(0)}</span>. That's{" "}
            <span className="text-[#FF3D8B] font-semibold">${(treeCostYear - yearlyCost).toFixed(0)} more per year</span> for a cardboard scent that quits in 5 days.
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Link to="/shop" data-testid="longevity-shop-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0]">
            Start your year of scents <ArrowRight size={14} />
          </Link>
          <Link to="/scent-quiz" data-testid="longevity-quiz-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white/80 hover:text-white text-xs uppercase tracking-widest">
            Take the scent quiz →
          </Link>
        </div>
      </div>
    </div>
  );
}
